import React from 'react'
import { useReactToPrint } from 'react-to-print'
import {
  InvoicesToPrint, PrintInvoicesStepsEnum, PrintInvoicesTypes, PrintInvoicesTypesObject, SectionLabelState,
} from '../PrintInvoicesSection.types'
import {
  Container, PrintButton, NextButton, SaveButton, ButtonsContainer, PrintingDetails, InvoicesCount, Observation, PrintArea,
} from './PrintingInvoicesBody.style'
import { actions } from 'src/features/receivement/ReceivementState'
import { useDispatch } from 'react-redux'
import '../../../../../styles/printingInvoicesBody.css'
import { ModalSaveReceipts } from '../ModalSaveReceipts/ModalSaveReceipts'
import { ReceivementInvoicesHtml, ReturnInvoice } from 'src/features/receivement/ReceivementTypes'

interface PrintingInvoicesBodyProps {
  setCurrentInvoicesType: React.Dispatch<React.SetStateAction<{sectionLabel: SectionLabelState, observations: string[]}>>
  observations: string[]
  invoicesToPrint: InvoicesToPrint[]
  currentInvoicesType: {sectionLabel: SectionLabelState, observations: string[]}
  currentInvoiceReturn?: ReturnInvoice[]
  currentReceivement: ReceivementInvoicesHtml | undefined
}

export const PrintingInvoicesBody = ({
  setCurrentInvoicesType, observations, invoicesToPrint, currentInvoicesType, currentInvoiceReturn, currentReceivement,
}: PrintingInvoicesBodyProps) => {
  const dispatch = useDispatch()

  const [currentPrintingInvoices, setCurrentPrintingInvoices] = React.useState<string[]>([])
  const [printingInvoices, setPrintingInvoices] = React.useState<InvoicesToPrint[]>(invoicesToPrint)
  const [nextButtonIsDisabled, setNextButtonIsDisabled] = React.useState<boolean>(true)
  const [saveButtonIsDisabled, setSaveButtonIsDisabled] = React.useState<boolean>(true)
  const [isOpenModalSaveReceipts, setIsOpenModalSaveReceipts] = React.useState<boolean>(false)
  const [printDone, setPrintDone] = React.useState<boolean>(false)

  const printAreaRef = React.useRef(null)

  const resolvePrintingInvoices = React.useCallback((isSetCurrent: boolean) => {
    const printingInvoice = printingInvoices.find(({ invoices, isPrinted }) => !isPrinted && invoices?.length)

    if (!printingInvoice) {
      dispatch(actions.setCurrentPrintInvoicesStep(PrintInvoicesStepsEnum.CONFIRM_PRINTS))
      return
    }

    printingInvoice.isPrinted = printDone

    if (isSetCurrent) {
      setCurrentPrintingInvoices(printingInvoice.invoices!)
      setCurrentInvoicesType(PrintInvoicesTypesObject[printingInvoice.type])
    } else {
      setPrintingInvoices(printingInvoices)
    }

    if (printDone) setPrintDone(false)
  }, [printingInvoices, dispatch, setCurrentInvoicesType, printDone])

  const printInvoicesAccepted = useReactToPrint({
    content: () => printAreaRef.current,
    copyStyles: false,
    onAfterPrint: () => {
      resolvePrintingInvoices(false)
      setNextButtonIsDisabled(false)
    },
  })

  const printInvoicesReturn = useReactToPrint({
    content: () => printAreaRef.current,
    copyStyles: false,
    onAfterPrint: () => {
      resolvePrintingInvoices(false)
      setSaveButtonIsDisabled(false)
      setNextButtonIsDisabled(true)
    },
  })

  const printInvoicesRejected = useReactToPrint({
    content: () => printAreaRef.current,
    copyStyles: false,
    onAfterPrint: () => {
      resolvePrintingInvoices(false)
      setNextButtonIsDisabled(false)
    },
  })

  const goToNextInvoices = () => {
    if (nextButtonIsDisabled) {
      return
    }
    setNextButtonIsDisabled(true)
    setPrintDone(true)
    resolvePrintingInvoices(true)
  }

  const saveReceipts = () => {
    setIsOpenModalSaveReceipts(true)
    setNextButtonIsDisabled(true)
  }

  const resolveInvoicesHtml = () => (
    currentPrintingInvoices.map((invoiceHtml, index) => (
      <div
        key={index}
        style={{
          pageBreakBefore: 'always',
          position: 'relative',
        }}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: invoiceHtml }}
      />
    ))
  )

  React.useEffect(() => {
    resolvePrintingInvoices(true)

    const fritsPrintingInvoices = printingInvoices.find((printingInvoice) => !printingInvoice.invoices?.length)

    if (fritsPrintingInvoices) fritsPrintingInvoices.isPrinted = true
    setPrintingInvoices(printingInvoices)
  }, [printingInvoices, resolvePrintingInvoices])

  const verifyPrintInvoiceType = () => {
    if (currentInvoicesType === PrintInvoicesTypesObject[PrintInvoicesTypes.ACCEPTED]) {
      printInvoicesAccepted()
    } else if (currentInvoicesType === PrintInvoicesTypesObject[PrintInvoicesTypes.RETURN]) {
      printInvoicesReturn()
    } else {
      printInvoicesRejected()
    }
  }

  React.useEffect(() => {
    function handleCloseModal(event) {
      if (event.keyCode === 27) {
        setIsOpenModalSaveReceipts(false)
      }
    }

    document.addEventListener('keydown', handleCloseModal)

    return () => {
      document.removeEventListener('keydown', handleCloseModal)
    }
  }, [setIsOpenModalSaveReceipts])

  return (
    <Container>
      <PrintingDetails>
        <InvoicesCount>Notas a imprimir: {currentPrintingInvoices?.length}</InvoicesCount>
        {observations.map((observation) => <Observation key={observation}>{observation}</Observation>)}
      </PrintingDetails>
      <ButtonsContainer>
        <PrintButton
          variant='contained'
          size='large'
          type='button'
          onClick={verifyPrintInvoiceType}
          data-testid='print-invoice-button'
        >
          Imprimir
        </PrintButton>
        {currentInvoicesType === PrintInvoicesTypesObject[PrintInvoicesTypes.RETURN]
        && (
        <SaveButton
          variant='contained'
          size='large'
          type='button'
          disabled={saveButtonIsDisabled}
          onClick={saveReceipts}
          data-testid='save-receipts-button'
        >
          Salvar canhotos
        </SaveButton>
        )}
        <NextButton
          variant='contained'
          size='large'
          type='button'
          disabled={nextButtonIsDisabled}
          onClick={goToNextInvoices}
          data-testid='next-step-or-print-button'
        >
          Próximo
        </NextButton>
      </ButtonsContainer>
      <PrintArea ref={printAreaRef}>
        {resolveInvoicesHtml()}
      </PrintArea>
      <ModalSaveReceipts
        setNextButtonIsDisabled={setNextButtonIsDisabled}
        setIsOpen={setIsOpenModalSaveReceipts}
        isOpen={isOpenModalSaveReceipts}
        currentInvoiceReturn={currentInvoiceReturn}
        currentReceivement={currentReceivement}
      />
    </Container>
  )
}
